import React, { useMemo, useState } from "react";
import { ArrowLeftRight, Camera, HardHat, History, Users, PackageOpen, Tag } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { api, fileUrl } from "../../lib/api";
import { BulkCheckbox } from "./BulkSelectContext";
import HistoryDialog from "./HistoryDialog";
import ItemDialog from "./ItemDialog";
import PSABatchDialog from "./PSABatchDialog";
import PhotoGalleryDialog from "./PhotoGalleryDialog";
import PSAHistoryDialog from "./PSAHistoryDialog";
import BulkAssignDialog from "./BulkAssignDialog";
import PickupTargetDialog from "./PickupTargetDialog";
import HandInPSADialog from "./HandInPSADialog";
import CategoriesManagerDialog from "./CategoriesManagerDialog";
import PSAModelBreakdown from "./PSAModelBreakdown";
import { toast } from "sonner";
import { psaItemComparator, psaStockFirstComparator } from "../../lib/psaConstants";
import PSACategoryIcon from "../../components/PSACategoryIcon";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

// Etichete categorii — ordinea cerută de user
const PSA_CAT_LABEL = {
  masca: "Măști",
  furtun: "Furtune",
  curea: "Curele",
  cizme: "Cizme",
  hanorac: "Hanorace",
  tricou: "Tricouri",
  bocanci: "Bocanci",
  pantaloni: "Pantaloni",
  geaca: "Geci",
  altele: "Altele",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getFullYear()}`;
  } catch { return "—"; }
};

export default function PSAView({ meta, items, cars, persons = [], historyCounts = {}, onChanged }) {
  const { canDo, isAdmin, user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [photoFor, setPhotoFor] = useState(null);
  const [confirmPickup, setConfirmPickup] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Dialog pentru creare comandă rapidă din badge-ul „🔴/🟡 Liber: N"
  const [orderDraft, setOrderDraft] = useState(null); // { cat, size, freeStock, name?, supplier? }
  // Dialog pentru predare în masă (1 stoc → N angajați)
  const [bulkAssignFor, setBulkAssignFor] = useState(null);
  // Toggle: „category" (default) sau „location" (vechiul comportament)
  const [groupMode, setGroupMode] = useState("category");
  // Filtrare categorie (când groupMode==="category"): "all" → toate, sau code-ul categoriei (masca, furtun, ...)
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Dialog predare PSA înapoi în magazie
  const [handInOpen, setHandInOpen] = useState(false);
  const [catsManagerOpen, setCatsManagerOpen] = useState(false);
  const isMagazioner = user?.role === "magazioner";
  const canPickup = isAdmin || isMagazioner;

  // Grupare pe categorie PSA (pentru groupMode === "category")
  const byCategory = useMemo(() => {
    const map = {};
    for (const it of items) {
      const cat = it.psa_category || "altele";
      (map[cat] ||= []).push(it);
    }
    // Sortare CONFORM CERINȚEI USER:
    //   1) Stocul (item-uri fără assignee_person_id) primul
    //   2) Apoi item-urile alocate
    //   3) În interiorul fiecărui grup: mărime CRESCĂTOR (XS→XXL sau 38→46), apoi nume
    for (const k of Object.keys(map)) {
      map[k].sort(psaStockFirstComparator);
    }
    return map;
  }, [items]);

  // Ordinea oficială a categoriilor (mască → furtun → curea → cizme → hanorac → tricou → bocanci → pantaloni → geacă → altele)
  // Conform cererii user: 1.Mască 2.Furtun 3.Curea 4.Cizme 5.Hanorace 6.Tricouri 7.Bocanci 8.Pantaloni
  const orderedCategoryKeys = useMemo(() => {
    const order = ["masca", "furtun", "curea", "cizme", "hanorac", "tricou", "bocanci", "pantaloni", "geaca", "altele"];
    const present = order.filter((c) => (byCategory[c] || []).length > 0);
    // Adaugă categorii necunoscute la sfârșit (defensive)
    for (const c of Object.keys(byCategory)) {
      if (!present.includes(c) && (byCategory[c] || []).length > 0) present.push(c);
    }
    return present;
  }, [byCategory]);

  // Grupare:
  //  - dacă item-ul are assignee_person_id → grup pe persoană („Predat la: <nume>")
  //  - altfel pe locație („MAGAZIE", „SERGHEI ACASA" etc.)
  //  - altfel în "__none__" (truly fără nimic — caz rar)
  const grouped = useMemo(() => {
    const map = {};
    for (const it of items) {
      let key;
      if (it.assignee_person_id) {
        key = `__person__:${it.assignee_person_id}`;
      } else if (it.location) {
        key = `__loc__:${it.location}`;
      } else {
        key = "__none__";
      }
      (map[key] ||= []).push(it);
    }
    // Sortare în interiorul fiecărui grup după categoria PSA (mască → furtun → curea → cizme → hanorac → tricou...)
    for (const k of Object.keys(map)) {
      map[k].sort(psaItemComparator);
    }
    return map;
  }, [items]);

  // Ordinea de afișare a grupurilor:
  //   1) Locații (alfabetic) — SUS: stocul fix din magazie/locații
  //   2) Persoane (alfabetic după nume) — JOS: ce este alocat fiecăruia
  //   3) __none__ la coadă (fără locație/persoană)
  const orderedGroupKeys = useMemo(() => {
    const personKeys = [];
    const locKeys = [];
    for (const k of Object.keys(grouped)) {
      if (k === "__none__") continue;
      if (k.startsWith("__person__:")) personKeys.push(k);
      else locKeys.push(k);
    }
    locKeys.sort((a, b) => a.localeCompare(b));
    personKeys.sort((a, b) => {
      const na = (grouped[a]?.[0]?.assignee || "").toLowerCase();
      const nb = (grouped[b]?.[0]?.assignee || "").toLowerCase();
      return na.localeCompare(nb, "ro");
    });
    const tail = grouped["__none__"]?.length ? ["__none__"] : [];
    return [...locKeys, ...personKeys, ...tail];
  }, [grouped]);

  const doPickup = async () => {
    // Legacy: helper-ul a fost înlocuit cu PickupTargetDialog (apel inline cu target_location / target_person_id).
    if (!confirmPickup) return;
    try {
      await api.post("/inventory/transfers", {
        action: "pickup",
        item_id: confirmPickup.id,
        note: "PSA preluat → locație implicită",
      });
      toast.success(`${confirmPickup.name} preluat`);
      setConfirmPickup(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la preluare");
    }
  };



  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          🦺 <strong>PSA</strong> = echipament de protecție personală (căști, ochelari, centuri, mănuși etc.). La preluare poți alege destinația: <strong>locația ta personalizată</strong>, <strong>altă locație custom</strong>, sau <strong>transfer direct la alt angajat</strong>.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle grupare */}
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs" data-testid="psa-group-toggle">
            <button
              type="button"
              onClick={() => setGroupMode("category")}
              className={`px-3 py-1.5 font-semibold transition-colors ${groupMode === "category" ? "bg-cyan-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="psa-group-by-category"
            >
              📂 Pe categorii
            </button>
            <button
              type="button"
              onClick={() => setGroupMode("location")}
              className={`px-3 py-1.5 font-semibold transition-colors border-l border-zinc-300 ${groupMode === "location" ? "bg-cyan-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="psa-group-by-location"
            >
              📍 Pe locații/persoane
            </button>
          </div>
          {canDo("INVENTAR", "add") && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const r = await api.post("/inventory/admin/cleanup-psa-names?dry_run=true");
                    const found = r.data?.found || 0;
                    if (found === 0) {
                      toast.info("Nicio denumire PSA cu sufix #N de curățat ✓");
                      return;
                    }
                    const preview = (r.data?.items || []).slice(0, 5).map((it) => `• ${it.old} → ${it.new}`).join("\n");
                    const more = found > 5 ? `\n…și încă ${found - 5}.` : "";
                    if (!window.confirm(`Vor fi redenumite ${found} echipamente PSA — folosind seria reală în loc de „#N":\n\n${preview}${more}\n\nAplici redenumirea?`)) return;
                    const r2 = await api.post("/inventory/admin/cleanup-psa-names?dry_run=false");
                    toast.success(`✓ Redenumit ${r2.data?.applied || 0} echipamente PSA`);
                    onChanged?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Eroare la curățare denumiri");
                  }
                }}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="psa-cleanup-names-btn"
                title="Curăță denumiri PSA cu sufix #N — înlocuiește cu seria reală"
              >
                🧹 Curăță denumiri
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    // Standardizează numele PSA per categorie (folosind primul preset)
                    const STANDARD = {
                      tricou: "ConVic Portocaliu",
                      hanorac: "Hanorac portocaliu cu glugă",
                    };
                    const cats = Object.keys(STANDARD);
                    // Dry run all categories
                    const previews = [];
                    let totalFound = 0;
                    for (const cat of cats) {
                      try {
                        const r = await api.post("/inventory/admin/standardize-psa-names", {
                          psa_category: cat,
                          new_name: STANDARD[cat],
                          dry_run: true,
                        });
                        const found = r.data?.found || 0;
                        totalFound += found;
                        if (found > 0) {
                          previews.push(`• ${cat} → „${STANDARD[cat]}": ${found} produs${found === 1 ? "" : "e"}`);
                        }
                      } catch (e) {
                        // skip
                      }
                    }
                    if (totalFound === 0) {
                      toast.info("Toate numele sunt deja standardizate ✓");
                      return;
                    }
                    if (!window.confirm(
                      `STANDARDIZARE NUME PSA:\n\n${previews.join("\n")}\n\nTOTAL: ${totalFound} produse vor fi redenumite.\n\nContinui?`
                    )) return;
                    let totalApplied = 0;
                    for (const cat of cats) {
                      try {
                        const r2 = await api.post("/inventory/admin/standardize-psa-names", {
                          psa_category: cat,
                          new_name: STANDARD[cat],
                          dry_run: false,
                        });
                        totalApplied += r2.data?.applied || 0;
                      } catch (e) {
                        // skip
                      }
                    }
                    toast.success(`✓ Standardizat ${totalApplied} produse PSA`);
                    onChanged?.();
                  }}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="psa-standardize-names-btn"
                  title='Redenumește toate tricourile la "ConVic Portocaliu" și hanoracele la "Hanorac portocaliu cu glugă"'
                >
                  🎯 Standardizează nume
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCatsManagerOpen(true)}
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="psa-cats-manager-btn"
              >
                <Tag size={14} className="mr-1.5"/> Categorii
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHandInOpen(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="psa-handin-btn"
              >
                <PackageOpen size={14} className="mr-1.5"/> Predă PSA
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                data-testid="psa-history-btn"
              >
                <History size={14} className="mr-1.5"/> Istoric PSA
              </Button>
              <Button onClick={() => setDialog({ mode: "add", initialType: "psa", initialPsaCategory: "masca" })} className="bg-cyan-700 hover:bg-cyan-800" data-testid="add-psa-masca-direct">
                😷 + Mască
              </Button>
              <Button onClick={() => setDialog({ mode: "add", initialType: "psa", initialPsaCategory: "furtun" })} className="bg-cyan-700 hover:bg-cyan-800" data-testid="add-psa-furtun-direct">
                🧯 + Furtun
              </Button>
              <Button onClick={() => setDialog({ mode: "add", initialType: "psa", initialPsaCategory: "curea" })} className="bg-cyan-700 hover:bg-cyan-800" data-testid="add-psa-curea-direct">
                🪢 + Curea
              </Button>
              <Button onClick={() => setDialog({ mode: "add", initialType: "psa" })} className="bg-[var(--primary)]" data-testid="add-psa-btn">
                <HardHat size={16} className="mr-1.5"/> PSA nou
              </Button>
            </>
          )}
        </div>
      </div>

      {/* === Selector categorie (vizibil DOAR în „Pe categorii") === */}
      {groupMode === "category" && orderedCategoryKeys.length > 0 && (
        <div className="card-flat p-2 flex flex-wrap items-center gap-1.5" data-testid="psa-category-filter">
          <span className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 mr-1">Filtrează:</span>
          <button
            type="button"
            onClick={() => setCategoryFilter("all")}
            className={`text-xs font-semibold px-3 py-1 rounded-full border-2 transition-all ${categoryFilter === "all" ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
            data-testid="psa-cat-filter-all"
          >
            ⚪ Toate ({items.length})
          </button>
          {orderedCategoryKeys.map((cat) => {
            const count = (byCategory[cat] || []).length;
            const label = PSA_CAT_LABEL[cat] || cat;
            const active = categoryFilter === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(active ? "all" : cat)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border-2 transition-all ${active ? "bg-cyan-600 text-white border-cyan-600 scale-[1.04]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
                data-testid={`psa-cat-filter-${cat}`}
              >
                <PSACategoryIcon cat={cat} size={14} className={active ? "text-white" : "text-cyan-700"}/>
                <span>{label}</span>
                <span className={`text-[10px] font-bold ${active ? "text-white/90" : "text-zinc-500"}`}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {items.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <HardHat size={48} className="mx-auto text-zinc-300 mb-3"/>
          <div className="font-display text-lg font-bold mb-1">Niciun PSA în inventar</div>
          <div className="text-sm text-zinc-500">Apasă <strong>"PSA nou"</strong> pentru a adăuga primul echipament de protecție.</div>
        </div>
      ) : groupMode === "category" ? (
        /* === VIEW pe CATEGORII: 1.Mască 2.Furtun 3.Curea 4.Cizme 5.Hanorace 6.Tricouri 7.Bocanci 8.Pantaloni === */
        orderedCategoryKeys
          .filter((cat) => categoryFilter === "all" || cat === categoryFilter)
          .map((cat) => {
          const list = byCategory[cat] || [];
          if (list.length === 0) return null;
          const catLabel = PSA_CAT_LABEL[cat] || cat;
          const totalQty = list.reduce((s, it) => s + (parseFloat(it.quantity) || 1), 0);
          const assignedCount = list.filter((it) => it.assignee_person_id).length;
          const stockCount = list.length - assignedCount;
          // Mapa: mărime → stoc liber (suma quantity din items FĂRĂ assignee_person_id)
          // Reutilizată în sizeBreakdown (header) + în fiecare rând (sub-text)
          const sizeStockMap = (() => {
            const m = new Map();
            for (const it of list) {
              if (it.assignee_person_id) continue;
              if (!it.psa_size) continue;
              const key = String(it.psa_size).trim();
              if (!key) continue;
              const rawQty = parseFloat(it.quantity);
              const qty = isNaN(rawQty) ? 1 : rawQty;
              m.set(key, (m.get(key) || 0) + qty);
            }
            return m;
          })();
          // Rezumat pe mărimi — DOAR pentru itemii din STOC (fără assignee_person_id)
          // ca să detectăm corect stocul disponibil pentru a comanda
          const sizeBreakdown = (() => {
            const m = sizeStockMap;
            if (m.size === 0) return null;
            // Sortează mărimile: numerice (42, 43, 44...) crescător, apoi alfabetice (S, M, L, XL...)
            const sizeOrder = { XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, XXXL: 7 };
            const arr = Array.from(m.entries()).sort(([a], [b]) => {
              const na = parseFloat(a), nb = parseFloat(b);
              const aIsNum = !isNaN(na), bIsNum = !isNaN(nb);
              if (aIsNum && bIsNum) return na - nb;
              if (aIsNum) return -1;
              if (bIsNum) return 1;
              const oa = sizeOrder[a.toUpperCase()] || 99;
              const ob = sizeOrder[b.toUpperCase()] || 99;
              if (oa !== ob) return oa - ob;
              return a.localeCompare(b);
            });
            return arr;
          })();
          return (
            <div key={cat} className="card-flat overflow-hidden" data-testid={`psa-cat-section-${cat}`}>
              <div className="bg-cyan-50 border-b border-cyan-200 px-4 py-3 flex items-center gap-3 flex-wrap">
                <PSACategoryIcon cat={cat} size={32} className="text-cyan-700"/>
                <h3 className="font-display text-lg font-bold text-cyan-900">{catLabel}</h3>
                <span className="text-xs px-2 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">
                  {list.length} {list.length === 1 ? "item" : "items"}
                  {totalQty !== list.length && ` (${totalQty} buc.)`}
                </span>
                {assignedCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                    👤 {assignedCount} la angajați
                  </span>
                )}
                {stockCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-semibold">
                    📦 {stockCount} în stoc
                  </span>
                )}
                {canDo("INVENTAR", "add") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDialog({ mode: "add", initialType: "psa", initialPsaCategory: cat })}
                    className="ml-auto text-xs h-7 border-cyan-400 text-cyan-800 hover:bg-cyan-100"
                    data-testid={`psa-cat-${cat}-add-btn`}
                    title={`Adaugă ${catLabel.toLowerCase()} în inventar`}
                  >
                    ➕ Produs
                  </Button>
                )}
                {sizeBreakdown && sizeBreakdown.length > 0 && (
                  <div className="basis-full flex items-center gap-1.5 flex-wrap" data-testid={`psa-cat-${cat}-sizes`}>
                    <span className="text-[10px] uppercase tracking-wider text-cyan-700 font-bold">📦 Stoc liber per mărime:</span>
                    {sizeBreakdown.map(([size, qty]) => {
                      const low = qty <= 2;
                      const out = qty <= 0;
                      const cls = out
                        ? "bg-red-50 border-red-400 text-red-900"
                        : low
                          ? "bg-amber-50 border-amber-400 text-amber-900"
                          : "bg-white border-cyan-300 text-cyan-900";
                      const tooltip = out
                        ? `STOC EPUIZAT pentru mărimea ${size}`
                        : low
                          ? `Stoc redus (${qty} ${qty === 1 ? "bucată" : "bucăți"}) — comandă curând`
                          : `${qty} ${qty === 1 ? "bucată" : "bucăți"} mărimea ${size}`;
                      return (
                        <span
                          key={size}
                          className={`text-[11px] px-1.5 py-0.5 rounded border font-mono font-semibold inline-flex items-center gap-0.5 ${cls}`}
                          title={tooltip}
                        >
                          {out && <span aria-hidden>🔴</span>}
                          {!out && low && <span aria-hidden>🟡</span>}
                          {size}: <strong>{qty}</strong>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <PSAModelBreakdown items={list} persons={persons} />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600">
                    <tr>
                      <th className="text-left p-2.5 w-10"></th>
                      <th className="text-left p-2.5">Denumire</th>
                      <th className="text-left p-2.5">Nr. serie</th>
                      <th className="text-center p-2.5">Mărime</th>
                      <th className="text-left p-2.5">Model / Furnizor</th>
                      <th className="text-left p-2.5">Unde se află</th>
                      <th className="text-left p-2.5">Date</th>
                      <th className="text-right p-2.5">Preț CHF</th>
                      <th className="text-center p-2.5">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {list.map((it) => {
                      const assignedToPerson = !!it.assignee_person_id;
                      const sergheiAcasa = !assignedToPerson && it.location === "SERGHEI ACASA";
                      return (
                        <tr
                          key={it.id}
                          onClick={() => setDialog({ mode: "edit", item: it })}
                          className="hover:bg-cyan-50/40 transition-colors cursor-pointer"
                          data-testid={`psa-cat-row-${it.id}`}
                          title="Click pentru detalii și istoric"
                        >
                          <td className="p-2.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPhotoFor(it); }}
                              className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200 hover:border-cyan-500 flex items-center justify-center"
                              title="Poze"
                            >
                              {(it.photo_ids || []).length ? (
                                <img src={fileUrl(it.photo_ids[0])} alt={it.name} className="w-full h-full object-cover"/>
                              ) : (
                                <HardHat size={16} className="text-zinc-400"/>
                              )}
                            </button>
                          </td>
                          <td className="p-2.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setHistoryFor(it); }}
                              className="font-semibold text-zinc-900 hover:text-cyan-700 hover:underline cursor-pointer text-left flex items-center gap-1 group"
                              title="Vezi istoricul complet (cumpărare, mutări, predări)"
                              data-testid={`psa-cat-row-${it.id}-name`}
                            >
                              {it.name}
                              <History size={11} className="opacity-0 group-hover:opacity-60 transition-opacity"/>
                              {(historyCounts[it.id] || 0) > 0 && (
                                <span
                                  className="text-[9px] font-bold tabular-nums bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded-full"
                                  title={`${historyCounts[it.id]} evenimente — click pentru detalii`}
                                  data-testid={`psa-cat-row-history-count-${it.id}`}
                                >📊 {historyCounts[it.id]}</span>
                              )}
                            </button>
                            {/* Badge inline cu locația/persoana — vizibil mereu, inclusiv pe mobil */}
                            <div className="mt-0.5">
                              {assignedToPerson ? (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold" title={it.assigned_at ? `Atribuit din ${fmtDate(it.assigned_at)}` : ""}>
                                  👤 {it.assignee || "(angajat)"}
                                  {it.assigned_at && <span className="text-emerald-700 font-normal opacity-90">· din {fmtDate(it.assigned_at)}</span>}
                                </span>
                              ) : sergheiAcasa ? (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-semibold">
                                  🏠 SERGHEI ACASA
                                  {it.assigned_at && <span className="text-cyan-700 font-normal opacity-90">· din {fmtDate(it.assigned_at)}</span>}
                                </span>
                              ) : it.location ? (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                                  📍 {it.location}
                                  {it.assigned_at && <span className="text-amber-700 font-normal opacity-90">· din {fmtDate(it.assigned_at)}</span>}
                                </span>
                              ) : (
                                <span className="text-[10px] text-zinc-400 italic">fără locație</span>
                              )}
                            </div>
                            {it.note && <div className="text-[10px] text-zinc-500 italic truncate max-w-[200px] mt-0.5" title={it.note}>{it.note}</div>}
                          </td>
                          <td className="p-2.5 font-mono text-xs">
                            {it.serial ? it.serial : <span className="italic text-zinc-400">fără serie</span>}
                          </td>
                          <td className="p-2.5 text-center">
                            {it.psa_size ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold text-xs">{it.psa_size}</span>
                                {(() => {
                                  const freeStock = sizeStockMap.get(String(it.psa_size).trim()) || 0;
                                  const lowOrOut = freeStock <= 2;
                                  if (freeStock <= 0) {
                                    return (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setOrderDraft({ cat, size: it.psa_size, freeStock, name: it.name, supplier: it.supplier }); }}
                                        className="text-[9px] text-red-700 font-semibold whitespace-nowrap hover:underline cursor-pointer"
                                        title="Click pentru a comanda — STOC EPUIZAT"
                                        data-testid={`psa-row-${it.id}-order-btn`}
                                      >
                                        🔴 Liber: 0 · 📞 Comandă
                                      </button>
                                    );
                                  }
                                  const cls = lowOrOut ? "text-amber-700" : "text-emerald-700";
                                  return lowOrOut ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setOrderDraft({ cat, size: it.psa_size, freeStock, name: it.name, supplier: it.supplier }); }}
                                      className={`text-[9px] ${cls} font-semibold whitespace-nowrap hover:underline cursor-pointer`}
                                      title={`Stoc redus (${freeStock} buc.) — click pentru a comanda`}
                                      data-testid={`psa-row-${it.id}-order-btn`}
                                    >
                                      🟡 Liber: {freeStock} · 📞 Comandă
                                    </button>
                                  ) : (
                                    <span className={`text-[9px] ${cls} font-semibold whitespace-nowrap`} title={`${freeStock} bucăți libere mărimea ${it.psa_size}`}>📦 Liber: {freeStock}</span>
                                  );
                                })()}
                              </div>
                            ) : <span className="text-zinc-400">—</span>}
                          </td>
                          <td className="p-2.5">
                            {it.supplier ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200 font-semibold">{it.supplier}</span>
                            ) : <span className="text-zinc-400 text-xs">—</span>}
                          </td>
                          <td className="p-2.5">
                            {assignedToPerson ? (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold" data-testid={`psa-cat-row-${it.id}-assignee`}>
                                👤 {it.assignee || "(angajat)"}
                              </span>
                            ) : sergheiAcasa ? (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-semibold">
                                🏠 SERGHEI ACASA
                              </span>
                            ) : it.location ? (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                                📍 {it.location}
                              </span>
                            ) : (
                              <span className="text-xs text-zinc-400 italic">fără locație</span>
                            )}
                          </td>
                          <td className="p-2.5 text-xs text-zinc-600">
                            <div className="space-y-0.5" data-testid={`psa-cat-row-${it.id}-dates`}>
                              <div className="flex items-center gap-1" title={it.purchase_date ? "Data achiziției (declarată)" : "Data înregistrării în sistem"}>
                                <span className="text-[9px] text-zinc-400 uppercase tracking-wider">
                                  {it.purchase_date ? "Achiziționat:" : "Înregistrat:"}
                                </span>
                                <span className="font-semibold text-zinc-700">{fmtDate(it.purchase_date || it.created_at)}</span>
                              </div>
                              {assignedToPerson && it.assigned_at && (
                                <div className="flex items-center gap-1" title={`La ${it.assignee || "(angajat)"} din data de ${fmtDate(it.assigned_at)}`}>
                                  <span className="text-[9px] text-emerald-600 uppercase tracking-wider">La pers. din:</span>
                                  <span className="font-semibold text-emerald-700">{fmtDate(it.assigned_at)}</span>
                                </div>
                              )}
                              {!assignedToPerson && it.assigned_at && it.location && (
                                <div className="flex items-center gap-1" title={`La locația ${it.location} din data de ${fmtDate(it.assigned_at)}`}>
                                  <span className="text-[9px] text-amber-600 uppercase tracking-wider">La loc. din:</span>
                                  <span className="font-semibold text-amber-700">{fmtDate(it.assigned_at)}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-2.5 text-right">
                            {it.price_chf != null ? (
                              <span className="text-emerald-700 font-bold text-xs">{(parseFloat(it.price_chf) * (parseFloat(it.quantity) || 1)).toFixed(2)}</span>
                            ) : <span className="text-zinc-400 text-xs">—</span>}
                          </td>
                          <td className="p-2.5">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {canPickup && assignedToPerson && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setConfirmPickup(it); }}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 hover:bg-cyan-200 font-bold"
                                  title="Preia PSA (merge la SERGHEI ACASA)"
                                  data-testid={`psa-cat-pickup-${it.id}`}
                                >
                                  <ArrowLeftRight size={10} className="inline"/> Preia
                                </button>
                              )}
                              {canDo("INVENTAR", "edit") && !assignedToPerson && (parseFloat(it.quantity) || 1) >= 1 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBulkAssignFor(it); }}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-bold"
                                  title="Predă din acest stoc la mai mulți angajați odată"
                                  data-testid={`psa-cat-bulk-assign-${it.id}`}
                                >
                                  <Users size={10} className="inline mr-0.5"/> Predă
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); setHistoryFor(it); }}
                                className="text-[10px] px-1.5 py-0.5 rounded text-zinc-600 hover:text-cyan-700 hover:bg-cyan-50"
                                title="Istoric mișcări"
                                data-testid={`psa-cat-history-${it.id}`}
                              >
                                <History size={11} className="inline"/>
                              </button>
                              {canDo("INVENTAR", "edit") && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDialog({ mode: "edit", item: it }); }}
                                  className="text-[10px] px-1.5 py-0.5 rounded text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                                  data-testid={`psa-cat-edit-${it.id}`}
                                >
                                  Editează
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      ) : (
        /* === VIEW pe LOCAȚII (sus) + PERSOANE alfabetic (jos) === */
        (() => {
          const firstLocIdx = orderedGroupKeys.findIndex((k) => k.startsWith("__loc__:"));
          const firstPersonIdx = orderedGroupKeys.findIndex((k) => k.startsWith("__person__:"));
          return orderedGroupKeys.map((groupKey, idx) => {
          const list = grouped[groupKey] || [];
          if (list.length === 0) return null;
          const isPerson = groupKey.startsWith("__person__:");
          const isLoc = groupKey.startsWith("__loc__:");
          const isNone = groupKey === "__none__";
          const loc = isLoc ? groupKey.replace("__loc__:", "") : "";
          const personName = isPerson ? (list[0]?.assignee || "(angajat)") : "";
          const isSergheiAcasa = isLoc && loc === "SERGHEI ACASA";
          return (
            <React.Fragment key={groupKey}>
              {idx === firstLocIdx && (
                <div className="flex items-center gap-2 mt-1" data-testid="psa-section-locations-hdr">
                  <span className="text-[11px] uppercase tracking-wider font-extrabold text-amber-900 bg-amber-100 border border-amber-300 px-3 py-1 rounded">📍 LOCAȚII (stoc disponibil)</span>
                  <div className="flex-1 h-px bg-amber-200"/>
                </div>
              )}
              {idx === firstPersonIdx && (
                <div className="flex items-center gap-2 mt-3" data-testid="psa-section-persons-hdr">
                  <span className="text-[11px] uppercase tracking-wider font-extrabold text-emerald-900 bg-emerald-100 border border-emerald-300 px-3 py-1 rounded">👤 ALOCATE ANGAJAȚILOR (alfabetic)</span>
                  <div className="flex-1 h-px bg-emerald-200"/>
                </div>
              )}
            <div className="card-flat p-4" data-testid={`psa-group-${groupKey}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                  isPerson ? "bg-emerald-100 text-emerald-800"
                  : isSergheiAcasa ? "bg-cyan-100 text-cyan-800"
                  : isNone ? "bg-zinc-100 text-zinc-600"
                  : "bg-amber-100 text-amber-800"
                }`}>
                  {isPerson ? `👤 Predat la: ${personName}` : isNone ? "📦 Fără locație" : `📍 ${loc}`}
                </span>
                <span className="text-xs text-zinc-500">{list.length} {list.length === 1 ? "echipament" : "echipamente"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {list.map((it) => (
                  <div key={it.id} className="relative border border-zinc-200 rounded-md p-3 flex flex-col gap-1.5 hover:border-[var(--primary)] transition-colors" data-testid={`psa-item-${it.id}`}>
                    <BulkCheckbox id={it.id} className="absolute top-1.5 left-1.5 z-10"/>
                    <div className="flex items-start gap-2">
                      {/* Photo thumbnail */}
                      <button
                        type="button"
                        onClick={() => setPhotoFor(it)}
                        className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200 hover:border-[var(--primary)] flex items-center justify-center"
                      >
                        {(it.photo_ids || []).length ? (
                          <img src={fileUrl(it.photo_ids[0])} alt={it.name} className="w-full h-full object-cover"/>
                        ) : (
                          <HardHat size={18} className="text-zinc-400"/>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => setHistoryFor(it)}
                          className="text-sm font-semibold truncate hover:text-cyan-700 hover:underline cursor-pointer text-left flex items-center gap-1 group w-full"
                          title="Click pentru istoric (data cumpărării + mutări)"
                          data-testid={`psa-item-${it.id}-name`}
                        >
                          <span className="truncate">{it.name}</span>
                          <History size={10} className="opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0"/>
                          {(historyCounts[it.id] || 0) > 0 && (
                            <span
                              className="ml-auto text-[9px] font-bold tabular-nums bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded-full flex-shrink-0"
                              title={`${historyCounts[it.id]} evenimente în istoric — click pentru detalii`}
                              data-testid={`psa-history-count-${it.id}`}
                            >📊 {historyCounts[it.id]}</span>
                          )}
                        </button>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {it.psa_size && (
                            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-600 text-white" title={`Mărime: ${it.psa_size}`} data-testid={`psa-item-${it.id}-size`}>
                              📏 {it.psa_size}
                            </span>
                          )}
                          {it.psa_category && (
                            <span className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-cyan-100 text-cyan-800">{it.psa_category}</span>
                          )}
                          {it.serial && (
                            <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-zinc-100 text-zinc-700 truncate max-w-[100px]" title={`Serie: ${it.serial}`}>SN {it.serial}</span>
                          )}
                        </div>
                        {it.supplier && <div className="text-[10px] text-blue-700 truncate mt-0.5" title={`Furnizor: ${it.supplier}`}>🏭 {it.supplier}</div>}
                        {it.note && <div className="text-[10px] text-zinc-500 truncate" title={it.note}>{it.note}</div>}
                        {it.assignee && (
                          <div className="mt-1 px-1.5 py-1 rounded bg-emerald-50 border border-emerald-200">
                            <div className="text-[10px] text-emerald-800 font-bold flex items-center gap-1 truncate">
                              <span>👤</span>
                              <span className="truncate">{it.assignee}</span>
                            </div>
                            {it.assigned_at && (
                              <div className="text-[9px] text-emerald-700">
                                📅 din <strong>{it.assigned_at.slice(8,10)}.{it.assigned_at.slice(5,7)}.{it.assigned_at.slice(0,4)}</strong>
                              </div>
                            )}
                            {it.assigned_by && <div className="text-[9px] text-emerald-600 truncate" title={`Predat de ${it.assigned_by}`}>de {it.assigned_by}</div>}
                          </div>
                        )}
                        {!it.assignee && it.assigned_at && (
                          <div className="text-[9px] text-zinc-500 mt-1 truncate" title={`La locație din ${it.assigned_at.slice(0,10)}`}>
                            📍 din <strong>{it.assigned_at.slice(8,10)}.{it.assigned_at.slice(5,7)}.{it.assigned_at.slice(0,4)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1 pt-1 border-t border-zinc-100">
                      {canPickup && !isSergheiAcasa && (
                        <button
                          onClick={() => setConfirmPickup(it)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 hover:bg-cyan-200 font-bold"
                          title="Preia PSA (merge la SERGHEI ACASA)"
                          data-testid={`psa-pickup-${it.id}`}
                        >
                          <ArrowLeftRight size={10} className="inline"/> Preia
                        </button>
                      )}
                      <button onClick={() => setPhotoFor(it)} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--primary-subtle)] text-[var(--primary)] hover:opacity-80" title="Poze">
                        <Camera size={10} className="inline"/>
                      </button>
                      {canDo("INVENTAR", "edit") && (
                        <button onClick={() => setDialog({ mode: "edit", item: it })} className="text-[10px] text-zinc-600 hover:text-zinc-900">Editează</button>
                      )}
                      <button onClick={() => setHistoryFor(it)} className="text-[10px] text-zinc-600 hover:text-zinc-900" title="Istoric">
                        <History size={10} className="inline"/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </React.Fragment>
          );
        });
        })()
      )}

      {/* Add/Edit dialog */}
      {dialog && (() => {
        const PREFIXED = new Set(["masca", "furtun", "curea"]);
        // Pentru ADD pe categorii cu prefix → folosim noul PSABatchDialog
        if (dialog.mode === "add" && dialog.initialPsaCategory && PREFIXED.has(dialog.initialPsaCategory)) {
          return (
            <PSABatchDialog
              defaultCategory={dialog.initialPsaCategory}
              meta={meta}
              onClose={() => setDialog(null)}
              onSaved={() => { setDialog(null); onChanged(); }}
            />
          );
        }
        return (
          <ItemDialog
            mode={dialog.mode}
            item={dialog.item}
            prefill={{ item_type: "psa", ...(dialog.initialPsaCategory ? { psa_category: dialog.initialPsaCategory } : {}) }}
            meta={meta}
            stores={[]}
            cars={cars || []}
            onClose={() => setDialog(null)}
            onSaved={() => { setDialog(null); onChanged(); }}
          />
        );
      })()}
      {historyFor && <HistoryDialog item={historyFor} onClose={() => setHistoryFor(null)}/>}
      {photoFor && <PhotoGalleryDialog item={photoFor} onClose={() => setPhotoFor(null)} onChanged={onChanged}/>}

      {/* Confirm pickup to SERGHEI ACASA */}
      {confirmPickup && (
        <PickupTargetDialog
          item={confirmPickup}
          locations={(meta?.locations || []).map((l) => ({ key: l, label: l }))}
          persons={persons}
          defaultLocationLabel={user?.pickup_location || "SERGHEI ACASA"}
          onClose={() => setConfirmPickup(null)}
          onDone={() => { setConfirmPickup(null); onChanged && onChanged(); }}
        />
      )}

      {historyOpen && <PSAHistoryDialog onClose={() => setHistoryOpen(false)} />}
      <BulkAssignDialog
        item={bulkAssignFor}
        open={!!bulkAssignFor}
        onClose={() => setBulkAssignFor(null)}
        onDone={() => { setBulkAssignFor(null); onChanged && onChanged(); }}
      />

      {/* Dialog rapid: comandă PSA când stocul e redus/epuizat */}
      {orderDraft && (
        <OrderPSADialog
          draft={orderDraft}
          onClose={() => setOrderDraft(null)}
          onCreated={() => { setOrderDraft(null); onChanged?.(); toast.success("Comanda creată — vezi în 'Comenzi'"); }}
        />
      )}

      {/* Dialog: Predă PSA înapoi în magazie */}
      {handInOpen && (
        <HandInPSADialog
          items={items}
          meta={meta}
          onClose={() => setHandInOpen(false)}
          onDone={() => { setHandInOpen(false); onChanged?.(); }}
        />
      )}

      {/* Dialog: Gestionează categorii (PSA + Echipament) */}
      {catsManagerOpen && (
        <CategoriesManagerDialog
          onClose={() => setCatsManagerOpen(false)}
          onChanged={() => onChanged?.()}
        />
      )}
    </div>
  );
}

const PSA_CAT_LABEL_LONG = {
  masca: "Mască", furtun: "Furtun", curea: "Curea",
  cizme: "Cizme", hanorac: "Hanorac", tricou: "Tricou",
  geaca: "Geacă", bocanci: "Bocanci", pantaloni: "Pantaloni", altele: "PSA",
};

function OrderPSADialog({ draft, onClose, onCreated }) {
  const defaultName = draft.name && draft.size
    ? `${PSA_CAT_LABEL_LONG[draft.cat] || draft.cat} ${draft.size}${draft.supplier ? ` ${draft.supplier}` : ""}`
    : `${PSA_CAT_LABEL_LONG[draft.cat] || draft.cat} ${draft.size}`;
  const [supplier, setSupplier] = useState(draft.supplier || "");
  const [itemName, setItemName] = useState(defaultName);
  const [quantity, setQuantity] = useState(Math.max(5, 5 - draft.freeStock));
  const [note, setNote] = useState(draft.freeStock <= 0
    ? `STOC EPUIZAT pentru mărimea ${draft.size}. Comandă urgentă.`
    : `Stoc redus (${draft.freeStock} buc.) pentru mărimea ${draft.size}. De comandat preventiv.`);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!supplier.trim()) { toast.error("Adaugă furnizorul"); return; }
    if (!itemName.trim()) { toast.error("Adaugă numele articolului"); return; }
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) { toast.error("Cantitate invalidă"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/orders", {
        supplier: supplier.trim(),
        item_name: itemName.trim(),
        quantity: qty,
        destination_text: "MAGAZIE",
        note: note.trim() || null,
      });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la creare comandă");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="psa-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PSACategoryIcon cat={draft.cat} size={26} className="text-cyan-700"/>
            <span>Comandă rapidă · {PSA_CAT_LABEL_LONG[draft.cat] || draft.cat} {draft.size}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs p-2.5 rounded bg-amber-50 border border-amber-300 text-amber-900">
            {draft.freeStock <= 0 ? (
              <>🔴 <strong>Stoc epuizat</strong> pentru mărimea <strong>{draft.size}</strong>. Comandă urgentă recomandată.</>
            ) : (
              <>🟡 <strong>{draft.freeStock} buc.</strong> rămase pentru mărimea <strong>{draft.size}</strong>. Comandă preventiv.</>
            )}
          </div>
          <div>
            <Label className="text-xs">Furnizor</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ex: Sundström, 3M, Hilti..." data-testid="psa-order-supplier"/>
          </div>
          <div>
            <Label className="text-xs">Articol (nume produs)</Label>
            <Input value={itemName} onChange={(e) => setItemName(e.target.value)} data-testid="psa-order-item-name"/>
          </div>
          <div>
            <Label className="text-xs">Cantitate de comandat</Label>
            <Input type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="psa-order-quantity"/>
          </div>
          <div>
            <Label className="text-xs">Notă (opțional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Detalii suplimentare..." data-testid="psa-order-note"/>
          </div>
          <div className="text-[10px] text-zinc-500 italic">
            Comanda va fi salvată în secțiunea „Comenzi" cu status <strong>pending</strong>. Destinația implicită este <strong>MAGAZIE</strong>.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="psa-order-cancel">Anulează</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="psa-order-submit">
            {saving ? "Se creează..." : "📞 Trimite comanda"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

